import React, { useEffect, useState, useRef, createElement } from 'react';
import { TranslationKey } from '../i18n/translations';
interface MermaidDiagramProps {
  code: string;
  isDark?: boolean;
  t: (key: TranslationKey) => string;
  exportMode?: boolean;
  containerWidth?: number;
}
let renderCounter = 0;
let mermaidModule: (typeof import('mermaid'))['default'] | null = null;
let mermaidLoading: Promise<(typeof import('mermaid'))['default']> | null = null;
function loadMermaid() {
  if (mermaidModule) return Promise.resolve(mermaidModule);
  if (mermaidLoading) return mermaidLoading;
  mermaidLoading = import('mermaid').then(mod => {
    mermaidModule = mod.default;
    return mermaidModule;
  });
  return mermaidLoading;
}
/**
 * Post-process Mermaid SVG to make it responsive:
 * - Preserve viewBox for proper scaling
 * - Remove fixed width/height so it fills container
 * - Add inline font styles for export compatibility
 */
function makeResponsiveSvg(rawSvg: string, exportFontSize?: number): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(rawSvg, 'image/svg+xml');
  const svg = doc.documentElement;
  const fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", "Helvetica Neue", Helvetica, Arial, sans-serif';
  // Ensure viewBox exists so the SVG can scale proportionally
  if (!svg.getAttribute('viewBox')) {
    const w = svg.getAttribute('width') || '800';
    const h = svg.getAttribute('height') || '600';
    const numW = parseFloat(w);
    const numH = parseFloat(h);
    if (numW && numH) {
      svg.setAttribute('viewBox', `0 0 ${numW} ${numH}`);
    }
  }
  // Remove fixed dimensions — let CSS control sizing
  svg.removeAttribute('width');
  svg.setAttribute('width', '100%');
  svg.removeAttribute('height');
  svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
  svg.setAttribute('style', 'max-width:100%;height:auto;');
  // Inject a <style> block so fonts survive png export
  const fontSize = exportFontSize ? `${exportFontSize}px` : 'inherit';
  const styleEl = doc.createElementNS('http://www.w3.org/2000/svg', 'style');
  styleEl.textContent = `
    text, tspan, foreignObject, foreignObject * {
      font-family: ${fontFamily} !important;
    }
    ${exportFontSize ? `
    text, tspan, .nodeLabel, .edgeLabel, .label, foreignObject div {
      font-size: ${fontSize} !important;
    }
    ` : ''}
  `;
  svg.insertBefore(styleEl, svg.firstChild);
  // Stamp inline font-family (and font-size for export) on every text node
  svg.querySelectorAll('text, tspan, foreignObject').forEach(el => {
    const prev = el.getAttribute('style') || '';
    const sizeStyle = exportFontSize ? `font-size:${fontSize};` : '';
    el.setAttribute('style', `font-family:${fontFamily};${sizeStyle}${prev}`);
  });
  return new XMLSerializer().serializeToString(svg);
}
export function MermaidDiagram({
  code,
  isDark = false,
  t,
  exportMode = false,
  containerWidth
}: MermaidDiagramProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const codeRef = useRef(code);
  const themeRef = useRef(isDark);
  const exportRef = useRef(exportMode);
  // Calculate font size based on container width for export
  const exportFontSize = exportMode ? containerWidth && containerWidth <= 375 ? 10 : 12 : undefined;
  useEffect(() => {
    const fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", "Helvetica Neue", Helvetica, Arial, sans-serif';
    const themeVars = isDark ? {
      primaryColor: '#3a3f4a',
      primaryTextColor: '#e8ecf0',
      primaryBorderColor: '#7b92a8',
      lineColor: '#5a6270',
      secondaryColor: '#2d3340',
      tertiaryColor: '#242830',
      background: '#242830',
      mainBkg: '#242830',
      secondBkg: '#2d3340',
      tertiaryBkg: '#3a3f4a',
      fontFamily
    } : {
      primaryColor: '#e8ecf0',
      primaryTextColor: '#2c3e50',
      primaryBorderColor: '#5b7c8d',
      lineColor: '#8b9caa',
      secondaryColor: '#f5f7f9',
      tertiaryColor: '#fafafa',
      background: '#fafafa',
      mainBkg: '#fafafa',
      secondBkg: '#f5f7f9',
      tertiaryBkg: '#e8ecf0',
      fontFamily
    };
    const flowchartConfig = exportMode ? {
      padding: 2,
      nodeSpacing: 15,
      rankSpacing: 25,
      useMaxWidth: true,
      htmlLabels: true
    } : {
      useMaxWidth: true,
      htmlLabels: true
    };
    loadMermaid().then(mermaid => {
      mermaid.initialize({
        startOnLoad: false,
        theme: 'base',
        securityLevel: 'strict',
        themeVariables: themeVars,
        fontFamily,
        fontSize: exportMode ? exportFontSize || 10 : 16,
        flowchart: flowchartConfig
      });
      setIsLoading(false);
    });
  }, [isDark, exportMode, exportFontSize]);
  useEffect(() => {
    if (isLoading) return;
    if (codeRef.current === code && themeRef.current === isDark && exportRef.current === exportMode && svg) {
      return;
    }
    codeRef.current = code;
    themeRef.current = isDark;
    exportRef.current = exportMode;
    const renderDiagram = async () => {
      try {
        const mermaid = await loadMermaid();
        const id = `mermaid-${++renderCounter}-${Date.now()}`;
        const {
          svg: renderedSvg
        } = await mermaid.render(id, code);
        const responsiveSvg = makeResponsiveSvg(renderedSvg, exportFontSize);
        setSvg(responsiveSvg);
        setError('');
      } catch (err) {
        console.error('Mermaid rendering error:', err);
        setError(String(err));
        setSvg('');
      }
    };
    renderDiagram();
  }, [code, isDark, exportMode, exportFontSize, svg, isLoading]);
  if (isLoading) {
    return <div style={{
      background: isDark ? '#2d3340' : '#fafafa',
      padding: '1rem',
      borderRadius: '0.75rem',
      border: isDark ? '1px solid #3a3f4a' : '1px solid #e8ecf0',
      textAlign: 'center',
      color: isDark ? '#7b92a8' : '#5b7c8d',
      fontWeight: 300,
      fontSize: '0.875rem'
    }} data-id="element-62">
        Loading diagram...
      </div>;
  }
  if (error) {
    return <div style={{
      color: isDark ? '#f87171' : '#c53030',
      padding: '1.5rem',
      background: isDark ? '#3a2020' : '#fff5f5',
      borderRadius: '0.75rem',
      border: isDark ? '1px solid #5a3030' : '1px solid #feb2b2',
      fontWeight: 300
    }} data-id="element-63">
        {t('mermaid.error')} {error}
      </div>;
  }
  return <div ref={containerRef} className="mermaid-container" style={{
    background: isDark ? '#2d3340' : '#fafafa',
    padding: exportMode ? '0.25rem' : '1rem',
    borderRadius: '0.75rem',
    border: isDark ? '1px solid #3a3f4a' : '1px solid #e8ecf0',
    overflow: 'hidden'
  }} dangerouslySetInnerHTML={{
    __html: svg
  }} data-id="element-64" />;
}